// REPORTS-UX-1 — the state↔params bridge.
//
// Reports.js owns the live report state (mode/filters/sort). The nav spine owns
// the location params (what a pin/recent/saved-view captures + restores). This
// hook keeps the two in sync in BOTH directions without a render loop:
//
//   OUT  local report state changes → write {report, view, label, subLabel} into
//        the CURRENT location IN PLACE (replaceParams, no history push) so the
//        breadcrumb + pin star reflect the live view; recordVisit (debounced).
//   IN   navigating to a pin/recent/saved-view (params change) → apply the
//        incoming view to local state, even when only the MODE differs (we react
//        to the params SIGNATURE, not just the report id).
//
// Loop safety: two refs form a barrier. The OUT effect writes params.view, which
// would re-fire IN — but IN short-circuits on its own echo (incomingSig ===
// lastWrittenSig). IN's applyParams mutates local state, re-firing OUT — which
// adopts the just-applied signature without re-dispatching. Signatures are
// compared (not object identity), so new object instances each render are fine.
import { useEffect, useRef, useCallback } from "react";
import { useNavigation } from "../components/nav/NavigationContext";
import { reportViewRefId, reportNavLabel, viewSignature } from "./reportRegistry";

export function useReportNav({ activeReport, viewState, setActiveReport, applyIncoming }) {
  const { current, replaceParams, recordVisit } = useNavigation();
  const lastWrittenSig = useRef(null);
  const lastAppliedSig = useRef(null);
  const recentTimer = useRef(null);

  // Debounced recents — avoid flooding /nav/recents while a user drags a date
  // or types a filter. The breadcrumb + pin (via replaceParams) stay immediate.
  const queueRecent = useCallback((entry) => {
    if (recentTimer.current) clearTimeout(recentTimer.current);
    recentTimer.current = setTimeout(() => recordVisit(entry), 650);
  }, [recordVisit]);
  useEffect(() => () => { if (recentTimer.current) clearTimeout(recentTimer.current); }, []);

  // ── IN: incoming params → local state ──────────────────────────────────────
  const incomingReport = current.params?.report;
  const incomingView = current.params?.view;
  const incomingSig = viewSignature(incomingReport, incomingView);
  useEffect(() => {
    if (!incomingReport) return;
    if (incomingSig === lastAppliedSig.current) return;  // already applied
    if (incomingSig === lastWrittenSig.current) return;  // our own OUT echo
    lastAppliedSig.current = incomingSig;
    if (incomingReport !== activeReport) setActiveReport(incomingReport);
    if (incomingView) applyIncoming(incomingReport, incomingView);
    /* eslint-disable-next-line */
  }, [incomingSig]);

  // ── OUT: local state → current location params (in place) + recents ─────────
  const outSig = viewSignature(activeReport, viewState);
  useEffect(() => {
    if (outSig === lastWrittenSig.current) return;       // nothing changed
    if (outSig === lastAppliedSig.current) {             // we just applied IN;
      lastWrittenSig.current = outSig;                   // adopt without re-writing nav
      return;
    }
    lastWrittenSig.current = outSig;
    const lbl = reportNavLabel(activeReport, viewState);
    replaceParams({ report: activeReport, view: viewState, label: lbl, subLabel: lbl }, current.key);
    queueRecent({
      kind: "report_view",
      ref_id: reportViewRefId(activeReport, viewState),
      page: "reports",
      label: lbl,
      params: { report: activeReport, view: viewState },
    });
    /* eslint-disable-next-line */
  }, [outSig]);
}
